import { ContextMenu } from "@z/ui";
import { PaneMenu, PaneArrangementItems } from "./PaneMenu";
import { useLayoutEffect, useRef, useState, type PointerEvent, type CSSProperties } from "react";
import { useDroppable, useDraggable } from "@z/dnd";
import { GripVertical } from "lucide-react";
import { activateTab, closePane, closeOtherTabs, closeAllTabs, focusPane } from "../model/ops";
import { activePane, findPane } from "../model/tree";
import type { Pane, TabGroup } from "../model/types";
import { cn } from "./cn";
import { dockCollision, tabCollision, type DropZone } from "./dnd";
import { useLayout } from "./LayoutContext";
import { paneTitle, type PaneRegistryEntry } from "./PaneRegistry";

export function TabGroupView({ group }: { group: TabGroup }) {
  const { tree, onChange } = useLayout();

  const active = activePane(group);
  const focused = active !== undefined && tree.focusedPaneId === active.id;

  const onPointerDown = (event: PointerEvent<HTMLElement>): void => {
    if (event.button !== 0 || !active || focused) return;
    onChange(focusPane(tree, active.id));
  };

  return (
    <section
      data-layout-group={group.id}
      tabIndex={-1}
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-bg outline-none"
      onPointerDown={onPointerDown}
    >
      <TabStrip group={group} />
      <div className="relative min-h-0 flex-1">
        {!active && (
          <div className="flex h-full items-center justify-center text-sm text-fg-muted">
            No open panes
          </div>
        )}
        <DockTargets group={group} />
      </div>
    </section>
  );
}

const ZONE_CLASS: Record<DropZone, string> = {
  center: "inset-0",
  left: "inset-y-0 left-0 w-1/2",
  right: "inset-y-0 right-0 w-1/2",
  top: "inset-x-0 top-0 h-1/2",
  bottom: "inset-x-0 bottom-0 h-1/2",
};

function DockTargets({ group }: { group: TabGroup }) {
  const { draggingPaneId, tree } = useLayout();
  const source = draggingPaneId ? findPane(tree, draggingPaneId) : undefined;
  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {(["center", "left", "right", "top", "bottom"] as const).map((zone) => (
        <DockTarget
          key={zone}
          groupId={group.id}
          zone={zone}
          disabled={
            !draggingPaneId ||
            (source?.group.id === group.id && (zone === "center" || group.tabs.length === 1))
          }
        />
      ))}
    </div>
  );
}

const TARGET_CLASS: Record<DropZone, string> = {
  center: "inset-[25%]",
  left: "inset-y-0 left-0 w-1/4",
  right: "inset-y-0 right-0 w-1/4",
  top: "inset-x-0 top-0 h-1/4",
  bottom: "inset-x-0 bottom-0 h-1/4",
};
function DockTarget({
  groupId,
  zone,
  disabled,
}: {
  groupId: string;
  zone: DropZone;
  disabled: boolean;
}) {
  const { ref, isDropTarget } = useDroppable({
    id: `dock:${groupId}:${zone}`,
    accept: "pane",
    collisionDetector: dockCollision,
    disabled,
    data: {
      groupId,
      edge: zone,
      keyboardAxis:
        zone === "center" ? null : zone === "left" || zone === "right" ? "horizontal" : "vertical",
      label: zone === "center" ? "Add to pane" : `Split ${zone}`,
    },
  });
  return (
    <>
      <div
        ref={ref}
        data-layout-dock-target={zone}
        className={cn("absolute", TARGET_CLASS[zone])}
      />
      {!disabled && isDropTarget && (
        <div
          data-layout-drop-zone={zone}
          className={cn(
            "absolute m-2 flex items-center justify-center rounded-lg border border-accent/40 bg-accent-muted/70 text-sm font-medium text-accent backdrop-blur-[2px]",
            ZONE_CLASS[zone],
          )}
        >
          {zone === "center" ? "Add to pane" : `Split ${zone}`}
        </div>
      )}
    </>
  );
}

function TabStrip({ group }: { group: TabGroup }) {
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const reveal = () => {
      const tab = Array.from(list.querySelectorAll<HTMLElement>("[data-layout-tab]")).find(
        (element) => element.dataset.layoutTab === group.activeTabId,
      );
      tab?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(list);
    return () => observer.disconnect();
  }, [group.activeTabId]);
  const { ref, isDropTarget } = useDroppable({
    id: `strip:${group.id}`,
    accept: "pane",
    collisionPriority: -1,
    data: { groupId: group.id, label: "End of tab strip", keyboardDisabled: group.tabs.length > 0 },
    collisionDetector: (input) => {
      const hit = dockCollision(input);
      return hit ? { ...hit, priority: -1 } : null;
    },
  });
  // Paint the divider inside the strip so the active tab's surface covers it.
  return (
    <div
      data-layout-tabstrip={group.id}
      className="flex h-10 shrink-0 items-stretch gap-1 overflow-hidden bg-surface px-2 pt-1.5 shadow-[inset_0_-1px_0_var(--color-border)]"
      ref={ref}
      style={isDropTarget ? { boxShadow: "inset 0 -2px var(--color-accent)" } : undefined}
    >
      <div
        role="tablist"
        ref={listRef}
        aria-label="Open tabs"
        className="scrollbar-quiet flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto overflow-y-hidden"
      >
        {group.tabs.map((tab, index) => (
          <Tab
            key={tab.id}
            tab={tab}
            index={index}
            groupId={group.id}
            active={tab.id === group.activeTabId}
          />
        ))}
      </div>
      <PaneMenu group={group} />
    </div>
  );
}

function Tab({
  tab,
  active,
  index,
  groupId,
}: {
  tab: Pane;
  active: boolean;
  index: number;
  groupId: string;
}) {
  const { tree, onChange, registry, onTabDoubleClick } = useLayout();
  const title = paneTitle(registry, tab);
  const entry = registry[tab.paneType];
  const Icon = entry?.icon;
  const { ref, handleRef, isDragSource } = useDraggable({
    id: tab.id,
    type: "pane",
    data: { label: title, groupId, keyboardNavigation: "targets" },
  });

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger
        render={
          <div
            data-layout-tab={tab.id}
            ref={ref}
            className={cn(
              "focus-ring-inset group relative flex max-w-48 shrink-0 cursor-pointer select-none items-center gap-2 rounded-t-md border border-b-0 px-3 pb-px text-sm transition-colors duration-fast",
              active
                ? "border-border bg-bg font-medium"
                : "border-transparent text-fg-muted hover:bg-bg/60 hover:text-fg",
              active && (tree.focusedPaneId === tab.id ? "text-accent" : "text-fg"),
              entry?.tabClassName?.(tab),
              isDragSource && "z-30 shadow-lg ring-1 ring-accent/40",
            )}
            onDoubleClick={() => onTabDoubleClick?.(tab)}
            onAuxClick={(event) => {
              if (event.button !== 1) return;
              event.preventDefault();
              onChange(closePane(tree, tab.id));
            }}
          >
            <TabDropSlot groupId={groupId} tabId={tab.id} index={index} after={false} />
            <TabDropSlot groupId={groupId} tabId={tab.id} index={index + 1} after />
            <button
              ref={handleRef}
              data-drag-handle={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              title="Drag tab · Space to pick up"
              className="flex min-w-0 touch-none cursor-grab items-center gap-2 self-stretch rounded-sm active:cursor-grabbing focus-ring-inset"
              onClick={() => onChange(activateTab(tree, tab.id))}
            >
              <span className="flex size-6 shrink-0 items-center justify-center text-fg-muted">
                {Icon ? <Icon className="size-4" /> : <GripVertical className="size-4" />}
              </span>
              <span className="truncate">{title}</span>
            </button>
            <button
              type="button"
              aria-label={`Close ${title}`}
              className="ml-1 flex size-5 shrink-0 items-center justify-center rounded-sm text-lg leading-none text-fg-muted hover:bg-border hover:text-fg focus-ring"
              onClick={(event) => {
                event.stopPropagation();
                onChange(closePane(tree, tab.id));
              }}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                className="size-3.5"
              >
                <path d="m6 6 12 12M6 18 18 6" />
              </svg>
            </button>
          </div>
        }
      />
      <ContextMenu.Content aria-label={`Tab actions for ${title}`}>
        <ContextMenu.Item onClick={() => onChange(closePane(tree, tab.id))}>
          Close tab
        </ContextMenu.Item>
        <ContextMenu.Item onClick={() => onChange(closeOtherTabs(tree, tab.id))}>
          Close other tabs
        </ContextMenu.Item>
        <ContextMenu.Item onClick={() => onChange(closeAllTabs(tree, groupId))}>
          Close all tabs in group
        </ContextMenu.Item>
        <ContextMenu.Separator />
        <PaneArrangementItems pane={tab} />
      </ContextMenu.Content>
    </ContextMenu.Root>
  );
}

function TabDropSlot({
  groupId,
  tabId,
  index,
  after,
}: {
  groupId: string;
  tabId: string;
  index: number;
  after: boolean;
}) {
  const { draggingPaneId } = useLayout();
  const { ref, isDropTarget } = useDroppable({
    id: `slot:${tabId}:${after ? "after" : "before"}`,
    accept: "pane",
    disabled: !draggingPaneId || draggingPaneId === tabId,
    collisionDetector: tabCollision,
    data: { groupId, index, after, keyboardDisabled: after, label: `Tab position ${index + 1}` },
  });
  return (
    <div
      ref={ref}
      data-layout-tab-slot={index}
      className={cn("pointer-events-none absolute inset-y-0 w-1/2", after ? "right-0" : "left-0")}
    >
      {isDropTarget && (
        <div className={cn("absolute inset-y-0 w-0.5 bg-accent", after ? "right-0" : "left-0")} />
      )}
    </div>
  );
}

export function PaneView({
  pane,
  focused,
  entry,
  hidden,
  style,
}: {
  pane: Pane;
  focused: boolean;
  entry: PaneRegistryEntry | undefined;
  hidden: boolean;
  style: CSSProperties;
}) {
  const { tree, onChange } = useLayout();
  const [visited, setVisited] = useState(!hidden);
  if (!hidden && !visited) setVisited(true);
  const Component = entry?.component ?? MissingPane;
  return (
    <div
      data-layout-pane={pane.id}
      data-focused={focused ? "" : undefined}
      hidden={hidden}
      style={style}
      className={hidden ? "hidden" : "absolute overflow-auto"}
      onPointerDown={(event) => {
        if (event.button === 0 && !focused) onChange(focusPane(tree, pane.id));
      }}
    >
      {visited && <Component pane={pane} focused={focused} />}
    </div>
  );
}

export function MissingPane({ pane }: { pane: Pane }) {
  return (
    <div
      data-layout-missing={pane.paneType}
      className="flex h-full items-center justify-center p-4 text-sm text-fg-muted"
    >
      Unknown pane type &ldquo;{pane.paneType}&rdquo;
    </div>
  );
}
